import React, { useCallback, useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Divider,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useToast } from "../../hooks/useToast";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { PushNotificationsConfigurationDetailForm } from "../../forms/PushNotificationsConfigurationDetailForm";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { LoadingIndicator } from "../../components/LoadingIndicator/LoadingIndicator";
import { PageContent } from "../../components/PageContent/PageContent";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { ChevronUpIcon } from "@chakra-ui/icons";
import { CopyText } from "../../components/Utilities/CopyText";
import { PlusIcon } from "../../styles/icons";
import { BusinessMenu } from "../../components/Business/BusinessMenu";

interface PushNotificationsConfigurationDetailProps {
  create?: boolean;
}

export const PushNotificationsConfigurationDetail: React.FC<PushNotificationsConfigurationDetailProps> =
  (props) => {
    const [searchParams] = useSearchParams();
    const { businessId } = useParams();
    const navigate = useNavigate();
    const { showErrorToast, showSuccessToast } = useToast();
    const adminApiClient = useAdminApiClient();

    const [configuration, setConfiguration] =
      useState<CherryHubAdminApi.PushNotificationsConfiguration | null>();
    const [loading, setLoading] = useState(!props.create);
    const [error, setError] = useState<string | undefined>(undefined);
    const [editMode, setEditMode] = useState<boolean>(
      props.create ??
        (searchParams.get("edit") !== null &&
        (searchParams.get("edit") === "" || searchParams.get("edit") === "true")
          ? true
          : false)
    );

    useEffect(() => {
      if (props.create) {
        // For create mode, initialize with default values and skip loading
        setConfiguration({
          id: "",
          BusinessId: businessId,
          NotificationTypeSettings: {
            GeneralNotificationEnabled: false,
          },
        });
        setLoading(false);
        return;
      }

      const fetchConfiguration = async () => {
        if (!businessId) {
          setError("Business ID is required");
          setLoading(false);
          return;
        }

        try {
          const result = await adminApiClient.getPushNotificationsConfiguration(
            businessId
          );
          if (result.ok && result.data) {
            setConfiguration(result.data);
          } else {
            // If no configuration exists, set configuration to null
            setConfiguration(null);
          }
        } catch (err) {
          setError("Failed to load Push Notifications Configuration");
          console.error(err);
        } finally {
          setLoading(false);
        }
      };

      fetchConfiguration();
    }, [businessId, adminApiClient, props.create]);

    const handleCancel = () => {
      if (props.create) {
        navigate(
          `/admin/businesses/${businessId}/push-notifications-configuration`
        );
      } else {
        setEditMode(false);
      }
    };

    const handleEdit = () => {
      // If no configuration exists yet, initialize with default values when entering edit mode
      if (!configuration) {
        setConfiguration({
          id: "",
          BusinessId: businessId,
          NotificationTypeSettings: {
            GeneralNotificationEnabled: false,
          },
        });
      }
      setEditMode(true);
    };

    const handleSuccess = useCallback(
      (
        updatedConfiguration?: CherryHubAdminApi.PushNotificationsConfiguration
      ) => {
        setConfiguration(updatedConfiguration);
        if (props.create) {
          showSuccessToast("Configuration updated.");
          navigate(
            `/admin/businesses/${businessId}/push-notifications-configuration`
          );
        } else {
          showSuccessToast("Configuration updated.");
          setEditMode(false);
        }
      },
      [businessId, navigate, props.create, showSuccessToast]
    );

    const handleFailure = () => {
      showErrorToast("Configuration update failed.");
    };

    if (loading) {
      return <LoadingIndicator />;
    }

    if (error && !editMode && !props.create) {
      return (
        <>
          <PageHeading>
            <PageHeading.Title>
              Push Notification Configuration
            </PageHeading.Title>
          </PageHeading>
          <PageContent>
            <VStack alignItems="flex-start" width="100%">
              <BusinessBreadcrumbs variant="pageHeading">
                <BreadcrumbItem>
                  <BreadcrumbLink
                    as={Link}
                    to={`/admin/businesses/${businessId}/push-notifications-configuration`}
                  >
                    Push Notification Configuration
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </BusinessBreadcrumbs>

              <VStack w="100%" p={4} spacing={2}>
                <Text fontStyle="italic" fontSize="sm">
                  Push Notification Configuration has not been set.
                </Text>
                <Button
                  size="sm"
                  leftIcon={<PlusIcon />}
                  onClick={() => {
                    navigate(
                      `/admin/businesses/${businessId}/push-notifications-configuration/new`
                    );
                  }}
                  colorScheme="cherryButton"
                >
                  Create Push Notification Configuration
                </Button>
              </VStack>
            </VStack>
          </PageContent>
        </>
      );
    }

    if (editMode || props.create) {
      return (
        <>
          <PageHeading>
            <PageHeading.Title>
              {props.create ? "Create" : "Edit"} Push Notification Configuration
            </PageHeading.Title>
          </PageHeading>
          <PageContent>
            <VStack alignItems="flex-start" width="100%">
              <BusinessBreadcrumbs variant="pageHeading">
                <BreadcrumbItem>
                  <BreadcrumbLink
                    as={Link}
                    to={`/admin/businesses/${businessId}/push-notifications-configuration`}
                  >
                    Push Notification Configuration
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </BusinessBreadcrumbs>

              {configuration && (
                <PushNotificationsConfigurationDetailForm
                  apiClient={adminApiClient}
                  businessId={businessId || ""}
                  configuration={configuration}
                  onCancel={handleCancel}
                  onSuccess={handleSuccess}
                  onFailure={handleFailure}
                />
              )}
            </VStack>
          </PageContent>
        </>
      );
    }

    // View mode - display the configuration details
    return (
      <>
        <PageHeading>
          <PageHeading.Title>Push Notification Configuration</PageHeading.Title>
        </PageHeading>
        <PageContent>
          <VStack alignItems="flex-start" width="100%">
            <BusinessBreadcrumbs variant="pageHeading">
              <BreadcrumbItem>
                <BreadcrumbLink
                  as={Link}
                  to={`/admin/businesses/${businessId}/push-notifications-configuration`}
                >
                  Push Notification Configuration
                </BreadcrumbLink>
              </BreadcrumbItem>
            </BusinessBreadcrumbs>
            <>
              <HStack w="100%">
                <Box flexGrow={1} pb={4}>
                  {configuration && configuration.Meta?.Id && (
                    <CopyText text={configuration.Meta.Id} hover adjustMargin>
                      <Text fontSize="md" color="cherryUi.600">
                        {configuration.Meta.Id}
                      </Text>
                    </CopyText>
                  )}
                </Box>
                <Box>
                  <BusinessMenu businessId={businessId!} onClick={handleEdit} />
                </Box>
              </HStack>

              {configuration && (
                <Accordion
                  w="100%"
                  allowToggle
                  allowMultiple
                  variant="section"
                  defaultIndex={[0]}
                >
                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <b>Notification Settings</b>
                            </Box>
                            {isExpanded ? (
                              <ChevronUpIcon color="white" fontSize="xl" />
                            ) : (
                              <AccordionIcon />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel width="100%">
                          <VStack w="100%" alignItems="flex-start">
                            <ViewControl
                              label="General Notifications"
                              value={
                                configuration.NotificationTypeSettings
                                  ?.GeneralNotificationEnabled
                                  ? "Enabled"
                                  : "Disabled"
                              }
                            />
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                </Accordion>
              )}
            </>
            {!configuration && (
              <VStack w="100%" p={4} spacing={2}>
                <Text>
                  No configuration has been created yet. Select 'Edit' to create
                  one.
                </Text>
              </VStack>
            )}
          </VStack>
        </PageContent>
      </>
    );
  };
